import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Database, Activity, Clock, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { rmsHealth, rmsDevices } from '../mock';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const RMSHealth = () => {
  const syncLogs = [
    { id: 1, timestamp: '2025-07-21 15:45:32', status: 'success', message: 'Data sync completed for 41 devices', duration: '2.3s' },
    { id: 2, timestamp: '2025-07-21 15:40:28', status: 'success', message: 'Data sync completed for 41 devices', duration: '2.1s' },
    { id: 3, timestamp: '2025-07-21 15:35:15', status: 'partial', message: 'Data sync completed with 2 errors', duration: '3.5s' },
    { id: 4, timestamp: '2025-07-21 15:30:42', status: 'success', message: 'Data sync completed for 41 devices', duration: '2.2s' },
    { id: 5, timestamp: '2025-07-21 15:25:18', status: 'error', message: 'Device RMS-PTN-005 timeout', duration: '5.0s' },
  ];

  const getLogStatusIcon = (status) => {
    if (status === 'success') return <CheckCircle className="w-4 h-4" style={{ color: '#10ac84' }} />;
    if (status === 'partial') return <AlertTriangle className="w-4 h-4" style={{ color: '#f39c12' }} />;
    return <XCircle className="w-4 h-4" style={{ color: '#e74c3c' }} />;
  };

  const getLogStatusBg = (status) => {
    if (status === 'success') return '#e8f5f1';
    if (status === 'partial') return '#fff4e6';
    return '#fee';
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold" style={{ color: '#0a3d62' }}>RMS API Health & Sync</h1>
        <p className="text-base mt-2" style={{ color: '#576574' }}>Monitor RMS integration health and data synchronization</p>
      </div>

      {/* Health Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#e8f5f1' }}>
                <Activity className="w-6 h-6" style={{ color: '#10ac84' }} />
              </div>
            </div>
            <div className="text-3xl font-bold mb-2" style={{ color: '#10ac84' }}>{rmsHealth.apiUptime}%</div>
            <div className="text-sm" style={{ color: '#576574' }}>API Uptime</div>
            <Progress value={rmsHealth.apiUptime} className="h-2 mt-3" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#e3f2fd' }}>
                <Database className="w-6 h-6" style={{ color: '#3498db' }} />
              </div>
            </div>
            <div className="text-3xl font-bold mb-2" style={{ color: '#0a3d62' }}>{rmsHealth.devicesConnected}/{rmsHealth.devicesTotal}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Devices Connected</div>
            <Progress value={(rmsHealth.devicesConnected / rmsHealth.devicesTotal) * 100} className="h-2 mt-3" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#f3e5f5' }}>
                <Clock className="w-6 h-6" style={{ color: '#9b59b6' }} />
              </div>
            </div>
            <div className="text-xl font-bold mb-2" style={{ color: '#0a3d62' }}>{rmsHealth.lastSyncSuccess}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Last Successful Sync</div>
          </CardContent>
        </Card>
      </div>

      {/* Device Status Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Device Status Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-64">
              <Pie
                data={{
                  labels: ['Online', 'Warning', 'Offline'],
                  datasets: [{
                    data: [
                      rmsDevices.filter(d => d.status === 'online').length,
                      rmsDevices.filter(d => d.status === 'warning').length,
                      rmsDevices.filter(d => d.status === 'offline').length
                    ],
                    backgroundColor: [
                      'rgba(16, 172, 132, 0.8)',
                      'rgba(243, 156, 18, 0.8)',
                      'rgba(231, 76, 60, 0.8)'
                    ],
                    borderColor: [
                      '#10ac84',
                      '#f39c12',
                      '#e74c3c'
                    ],
                    borderWidth: 1,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      titleColor: '#fff',
                      bodyColor: '#fff',
                    }
                  }
                }}
              />
            </div>
            <div className="flex flex-col justify-center space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: '#e8f5f1' }}>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5" style={{ color: '#10ac84' }} />
                  <span className="font-semibold" style={{ color: '#10ac84' }}>Online</span>
                </div>
                <span className="text-lg font-bold" style={{ color: '#0a3d62' }}>{rmsDevices.filter(d => d.status === 'online').length}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: '#fff4e6' }}>
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5" style={{ color: '#f39c12' }} />
                  <span className="font-semibold" style={{ color: '#f39c12' }}>Warning</span>
                </div>
                <span className="text-lg font-bold" style={{ color: '#0a3d62' }}>{rmsDevices.filter(d => d.status === 'warning').length}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: '#fee' }}>
                <div className="flex items-center gap-3">
                  <XCircle className="w-5 h-5" style={{ color: '#e74c3c' }} />
                  <span className="font-semibold" style={{ color: '#e74c3c' }}>Offline</span>
                </div>
                <span className="text-lg font-bold" style={{ color: '#0a3d62' }}>{rmsDevices.filter(d => d.status === 'offline').length}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Ingestion & Errors */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Data Ingestion Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <div className="text-6xl font-bold mb-4" style={{ color: '#10ac84' }}>{rmsHealth.dataIngestionRate}</div>
              <Progress value={95} className="h-3 max-w-md mx-auto" />
              <div className="mt-4 text-sm" style={{ color: '#576574' }}>Excellent performance - Target: {'>'} 90%</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Sync Errors (24h)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-4" style={{ backgroundColor: rmsHealth.syncErrors24h < 5 ? '#e8f5f1' : '#fff4e6' }}>
                <div className="text-4xl font-bold" style={{ color: rmsHealth.syncErrors24h < 5 ? '#10ac84' : '#f39c12' }}>
                  {rmsHealth.syncErrors24h}
                </div>
              </div>
              <Badge variant="outline" style={{ backgroundColor: rmsHealth.syncErrors24h < 5 ? '#e8f5f1' : '#fff4e6', color: rmsHealth.syncErrors24h < 5 ? '#10ac84' : '#f39c12' }}>
                {rmsHealth.syncErrors24h < 5 ? 'Within Acceptable Range' : 'Needs Attention'}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sync Logs */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Sync Logs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {syncLogs.map((log) => (
              <div key={log.id} className="p-4 rounded-lg border flex items-start gap-4" style={{ backgroundColor: getLogStatusBg(log.status) }}>
                <div className="mt-1">
                  {getLogStatusIcon(log.status)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-sm" style={{ color: '#576574' }}>{log.timestamp}</span>
                    <Badge variant="outline" className="text-xs">
                      {log.duration}
                    </Badge>
                  </div>
                  <div className="font-medium" style={{ color: '#0a3d62' }}>{log.message}</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* API Endpoints Health */}
      <Card>
        <CardHeader>
          <CardTitle>API Endpoints Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#10ac84' }} />
                  <span className="font-mono text-sm font-semibold">/api/gcrt/devices</span>
                </div>
                <Badge variant="outline" style={{ backgroundColor: '#e8f5f1', color: '#10ac84' }}>HEALTHY</Badge>
              </div>
              <div className="text-sm" style={{ color: '#576574' }}>Device data retrieval endpoint - 99.2% uptime</div>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#10ac84' }} />
                  <span className="font-mono text-sm font-semibold">/api/gcrt/telemetry</span>
                </div>
                <Badge variant="outline" style={{ backgroundColor: '#e8f5f1', color: '#10ac84' }}>HEALTHY</Badge>
              </div>
              <div className="text-sm" style={{ color: '#576574' }}>Real-time telemetry data stream - 98.8% uptime</div>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#10ac84' }} />
                  <span className="font-mono text-sm font-semibold">/api/gcrt/health</span>
                </div>
                <Badge variant="outline" style={{ backgroundColor: '#e8f5f1', color: '#10ac84' }}>HEALTHY</Badge>
              </div>
              <div className="text-sm" style={{ color: '#576574' }}>Health check endpoint - 99.9% uptime</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RMSHealth;
